import { Maybe } from '@common/neo.tax';
import { useMemo } from 'react';
import { CellProps, Column, type UseRowSelectInstanceProps } from 'react-table';

import type { SyncedProviders } from '@neo.tax/shared/graphql';

export type CreateColumns<T extends object> = ({
  handleClickRow,
  handleSelectAllRows,
  handleSelectRow,
  rowCount,
  selectedRowIds,
  syncedProviders,
}: {
  handleSelectAllRows: (checked: boolean) => void;
  handleSelectRow: (cell: CellProps<T, Maybe<string>> & UseRowSelectInstanceProps<T>) => (checked: boolean) => void;
  handleClickRow: (cell: CellProps<T, Maybe<string>> & UseRowSelectInstanceProps<T>) => () => void;
  rowCount: number;
  selectedRowIds: string[];
  syncedProviders?: SyncedProviders | undefined;
}) => Column<T>[];

interface UseExpenseTableColumnsProps<T extends object> {
  createColumns: CreateColumns<T>;
  onClickRow: (item: T) => void;
  onSelectAllRows: (checked: boolean) => void;
  onSelectRow: (checked: boolean, item: T) => void;
  rowCount: number;
  selectedRowIds: string[];
  syncedProviders?: SyncedProviders;
}

export const useExpenseTableColumns = <Row extends object>({
  createColumns,
  onClickRow,
  onSelectAllRows,
  onSelectRow,
  rowCount,
  selectedRowIds,
  syncedProviders,
}: UseExpenseTableColumnsProps<Row>): Column<Row>[] => {
  const columns = useMemo<Column<Row>[]>(() => {
    const handleClickRow = (cell: CellProps<Row, Maybe<string>> & UseRowSelectInstanceProps<Row>) => (): void => {
      // de-highlight all rows and highlight the current row that was clicked
      cell.toggleAllRowsSelected(false);
      cell.toggleRowSelected(cell.row.id, true);

      // call the onClickRow handler with the current row
      onClickRow(cell.row.original);
    };

    const handleSelectRow =
      (cell: CellProps<Row, Maybe<string>> & UseRowSelectInstanceProps<Row>) =>
      (checked: boolean): void => {
        onSelectRow(checked, cell.row.original);
      };

    const handleSelectAllRows = (checked: boolean): void => {
      onSelectAllRows(checked);
    };

    return createColumns({
      selectedRowIds,
      rowCount,
      handleSelectAllRows,
      handleSelectRow,
      handleClickRow,
      syncedProviders,
    });
  }, [createColumns, onClickRow, onSelectAllRows, onSelectRow, syncedProviders, rowCount, selectedRowIds]);

  return columns;
};
